// Steady width for customizable `.gl-select*` dropdowns (#16878).
//
// Since #16688 Chrome draws `.gl-select`, `.gl-select-sm`, `.gl-select-lg` and
// every alias that @extends them as a customizable select
// (`appearance: base-select`, components/_forms.scss). A NATIVE select is as
// wide as its LONGEST option; a base-select that gets no width from its layout
// is as wide as the option CURRENTLY SHOWN — a toolbar filter showing "All" was
// 60px and jumped to 194px when "Mortgage Protection Insurance" was picked,
// shoving everything beside it. No CSS reproduces the native rule: the option
// list lives in a top-layer popover that never takes part in the control's
// layout, and `field-sizing: fixed` does not change it (Chrome 155).
//
// So this installs ONE document-level runtime that gives each such select a
// `min-width` equal to its longest option (text measured with the select's own
// computed font, plus its horizontal padding and border — the chevron lives in
// the right padding). It is written as `min(<px>, 100%)` so the existing
// `max-width: 100%` (a long option list must never push a control past a 390px
// dialog, PR #12175) still holds, and capped at any px `max-width` the select
// carries (`.gl-filter-panel-saved-select`).
//
// Only CONTENT-SIZED selects are touched. A select whose layout sets its width
// — `w-100`, `width: 100%` (`.gl-filter-select`, the panel selects), a fixed
// width (the 70px country code), a flex/grid cell that stretches it — is left
// exactly as it is. The test: the select's rendered width equals the width of
// the option it shows (±1.5px). A select that is not rendered yet (closed
// modal, hidden tab) is judged the moment it first gets a size
// (ResizeObserver). Once a select is steady its width is re-measured when its
// option list changes (MutationObserver) and when web fonts finish loading.
//
// Browsers without customizable select keep a native select, which is steady
// already — the runtime installs nothing there. Covers HAML, Stimulus and React
// selects alike: they all live in the one document.

const STEADY_ATTR = "data-gl-steady-width";
const JUDGED_ATTR = "data-gl-steady-width-judged";
const CONTENT_SIZED_TOLERANCE_PX = 1.5;

let installed = false;
let canvasContext = null;

function px(value) {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : 0;
}

function fontOf(style) {
  if (style.font) return style.font;
  return [style.fontStyle, style.fontVariant, style.fontWeight, style.fontSize, style.fontFamily]
    .filter(Boolean)
    .join(" ");
}

// Canvas text measurement with the select's own font. Returns null when no
// 2D canvas is available (the runtime then does nothing).
export function canvasMeasureText(text, style) {
  if (!canvasContext) {
    const canvas = document.createElement("canvas");
    canvasContext = canvas.getContext ? canvas.getContext("2d") : null;
    if (!canvasContext) return null;
  }
  canvasContext.font = fontOf(style);
  if ("letterSpacing" in canvasContext) canvasContext.letterSpacing = style.letterSpacing || "0px";
  return canvasContext.measureText(text).width;
}

function optionLabel(option) {
  return (option.label || option.textContent || "").trim();
}

// The px a box adds around its text, in the select's own box-sizing.
function chromeWidth(style) {
  const padding = px(style.paddingLeft) + px(style.paddingRight);
  const border = px(style.borderLeftWidth) + px(style.borderRightWidth);
  return style.boxSizing === "border-box" ? padding + border : 0;
}

export function steadyWidthFor(select, { measureText = canvasMeasureText, getStyle = getComputedStyle } = {}) {
  const style = getStyle(select);
  let longest = 0;
  for (const option of Array.from(select.options)) {
    const width = measureText(optionLabel(option), style);
    if (width === null) return null;
    longest = Math.max(longest, width);
  }
  let steady = Math.ceil(longest + chromeWidth(style));
  if (style.maxWidth && style.maxWidth.endsWith("px")) steady = Math.min(steady, px(style.maxWidth));
  if (style.minWidth && style.minWidth.endsWith("px")) steady = Math.max(steady, px(style.minWidth));
  return steady;
}

// True when the select's width comes from the option it shows rather than
// from its layout. Its computed width is the used width (border-box under the
// app's global box-sizing), in the select's own CSS px — immune to the
// desktop body zoom.
export function isContentSized(select, { measureText = canvasMeasureText, getStyle = getComputedStyle } = {}) {
  const style = getStyle(select);
  const width = px(style.width);
  if (width <= 0) return false;
  const shown = select.selectedOptions && select.selectedOptions[0];
  const text = shown ? optionLabel(shown) : "";
  const textWidth = measureText(text, style);
  if (textWidth === null) return false;
  return Math.abs(width - (textWidth + chromeWidth(style))) <= CONTENT_SIZED_TOLERANCE_PX;
}

export function applySteadyWidth(select, deps) {
  const steady = steadyWidthFor(select, deps);
  if (steady === null) return;
  select.style.minWidth = `min(${steady}px, 100%)`;
  select.setAttribute(STEADY_ATTR, String(steady));
}

function isBaseSelect(select, getStyle = getComputedStyle) {
  if (select.multiple || select.size > 1) return false;
  return getStyle(select).appearance === "base-select";
}

export function createSelectSteadyWidth({
  root = document.documentElement,
  measureText = canvasMeasureText,
  getStyle = (el) => getComputedStyle(el),
  isCandidate = (select) => isBaseSelect(select, getStyle),
  ResizeObserverImpl = globalThis.ResizeObserver,
  MutationObserverImpl = globalThis.MutationObserver,
  fonts = document.fonts,
} = {}) {
  const deps = { measureText, getStyle };

  // A select is judged once, the first time it has a rendered size.
  const resizeObserver = ResizeObserverImpl
    ? new ResizeObserverImpl((entries) => {
        for (const entry of entries) judge(entry.target);
      })
    : null;

  function judge(select) {
    if (select.hasAttribute(JUDGED_ATTR) || !select.isConnected) return;
    if (px(getStyle(select).width) <= 0) return; // not rendered yet — wait for a size
    select.setAttribute(JUDGED_ATTR, "");
    resizeObserver?.unobserve(select);
    if (isContentSized(select, deps)) applySteadyWidth(select, deps);
  }

  function consider(select) {
    if (select.hasAttribute(STEADY_ATTR)) {
      applySteadyWidth(select, deps);
      return;
    }
    if (select.hasAttribute(JUDGED_ATTR) || !isCandidate(select)) return;
    judge(select);
    if (!select.hasAttribute(JUDGED_ATTR)) resizeObserver?.observe(select);
  }

  function scan(node) {
    if (!(node instanceof Element)) return;
    if (node instanceof HTMLSelectElement) consider(node);
    else node.querySelectorAll("select").forEach(consider);
  }

  function handleMutations(records) {
    const touched = new Set();
    for (const record of records) {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      // An option added, removed or relabelled inside an already steady select.
      // A class change on the select itself can make it a candidate.
      const owner = target?.closest("select");
      if (owner) {
        if (owner.hasAttribute(STEADY_ATTR) || (record.type === "attributes" && owner === target)) touched.add(owner);
        continue;
      }
      record.addedNodes?.forEach((node) => scan(node));
    }
    touched.forEach(consider);
  }

  const mutationObserver = MutationObserverImpl ? new MutationObserverImpl(handleMutations) : null;

  function refreshSteady() {
    root.querySelectorAll(`select[${STEADY_ATTR}]`).forEach((select) => applySteadyWidth(select, deps));
  }

  return {
    start() {
      scan(root);
      mutationObserver?.observe(root, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["class", "label"],
      });
      fonts?.addEventListener?.("loadingdone", refreshSteady);
      fonts?.ready?.then(refreshSteady);
    },
    stop() {
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      fonts?.removeEventListener?.("loadingdone", refreshSteady);
    },
    handleMutations,
    judge,
    scan,
  };
}

export function initSelectSteadyWidth() {
  if (installed) return;
  if (typeof CSS === "undefined" || !CSS.supports?.("appearance", "base-select")) return;
  installed = true;
  const start = () => createSelectSteadyWidth().start();
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
